import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "./Ui";
import EmailMeButton from "./EmailMeButton";
import { Linkedin } from "lucide-react";
import { useContent } from "../content/ContentContext";

const items = [
  { label: "Home", to: "/#home", hash: "#home" },
  { label: "About", to: "/#about", hash: "#about" },
  { label: "My Approach", to: "/#approach", hash: "#approach" },
  { label: "Services", to: "/#services", hash: "#services" },
  { label: "Pricing", to: "/#pricing", hash: "#pricing" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Contact", to: "/#contact", hash: "#contact" },
];

const SECTION_IDS = items.filter((i) => i.hash).map((i) => i.hash.slice(1));

function isItemActive(item, location, activeSection) {
  if (item.hash) return location.pathname === "/" && activeSection === item.hash.slice(1);
  return location.pathname.startsWith(item.to);
}

function ScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let raf = 0;
    const compute = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(100, Math.max(0, (window.scrollY / max) * 100)) : 0);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(compute); };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);
  return (
    <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[3px] bg-transparent" data-testid="scroll-progress-track">
      <div className="h-full bg-plum transition-[width] duration-100 ease-linear" style={{ width: `${progress}%` }} data-testid="scroll-progress-bar" />
    </div>
  );
}

// Tracks which homepage section is currently on screen (just below the sticky header).
function useActiveSection(pathname) {
  const [active, setActive] = useState("home");
  useEffect(() => {
    if (pathname !== "/") return undefined;
    let raf = 0;
    const compute = () => {
      raf = 0;
      const probe = 120; // px from top of viewport, below the header
      let current = "home";
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= probe) current = id;
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) current = "contact";
      setActive(current);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(compute); };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [pathname]);
  return active;
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { content } = useContent();
  const activeSection = useActiveSection(location.pathname);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const linkCls = (active) =>
    `text-sm font-medium transition-colors duration-150 hover:text-plum ${active ? "text-plum" : "text-ink"}`;

  return (
    <header
      data-testid="site-header"
      className="no-print sticky top-0 z-40 border-b border-line bg-cream/90 backdrop-blur-md"
    >
      <ScrollProgress />
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <Logo />
        <nav aria-label="Main navigation" className="hidden items-center gap-5 xl:flex">
          {items.map((item) => (
            <Link key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`} className={linkCls(isItemActive(item, location, activeSection))} aria-current={isItemActive(item, location, activeSection) ? "true" : undefined}>
              {item.label}
            </Link>
          ))}
          <Link
            data-testid="lets-talk-nav-button"
            to="/#contact"
            className="rounded-full bg-plum px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-ink"
          >
            Let&rsquo;s talk
          </Link>
        </nav>
        <button
          data-testid="mobile-menu-toggle"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 rounded-xl border border-line xl:hidden"
        >
          <span
            className={`h-0.5 w-5 bg-ink transition-transform duration-200 ${open ? "translate-y-2 rotate-45" : ""}`}
          />
          <span className={`h-0.5 w-5 bg-ink transition-opacity ${open ? "opacity-0" : ""}`} />
          <span
            className={`h-0.5 w-5 bg-ink transition-transform duration-200 ${open ? "-translate-y-2 -rotate-45" : ""}`}
          />
        </button>
      </div>
      {open && (
        <nav
          aria-label="Mobile navigation"
          data-testid="mobile-menu"
          className="border-t border-line bg-cream px-4 pb-6 pt-2 xl:hidden"
        >
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className={`block border-b border-line py-3 text-base font-medium ${isItemActive(item, location, activeSection) ? "text-plum" : "text-ink"}`}
            >
              {item.label}
            </Link>
          ))}
          <Link
            data-testid="lets-talk-mobile-button"
            to="/#contact"
            className="mt-4 inline-flex rounded-full bg-plum px-6 py-3 text-sm font-semibold text-white"
          >
            Let&rsquo;s talk
          </Link>
          <div className="mt-6 border-t border-line pt-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">Email</p>
            <a
              href={`mailto:${content.site.email}`}
              data-testid="mobile-email-link"
              className="mt-2 block break-all text-base font-medium text-ink hover:text-plum"
            >
              {content.site.email}
            </a>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <EmailMeButton testid="email-me-mobile-button" />
              <a
                href={content.site.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-linkedin-link"
                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold text-plum underline-offset-4 transition-colors duration-200 hover:bg-lavender/60 hover:underline focus:outline-none focus-visible:bg-lavender/60 focus-visible:ring-2 focus-visible:ring-accent"
              >
                <Linkedin aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
                LinkedIn
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
